import React, { useMemo, useState } from 'react';
import useLanguage from '@op/hooks/useLanguage';
import { Button, Form } from 'tea-component';
import DateSelect from '@op/components/FilterCommon/widgets/DateSelect';
import { getLocale } from 'umi';
import moment, { Moment } from 'moment';
import { paramsToMomentMap } from '@op/utils/time';
import GPT from '@op/components/GPT';
import RangePicker from '@home/components/RangePicker';
import { defaultRangePickerValue } from '@op/components/FilterCommon/constants/date';
import './index.scss';
import { initialUpperCase } from '@op/utils';
import { useCreateRepeatSummaryTask } from '../../hooks';
import { getLinkDomain } from '@op/utils/url';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import { getAiSaveEmailList } from '@op/components/GPT/utils';

export default function RepeatSetting(props: {
  gameInfo: any;
  filter: any;
  toggleVisible: () => void;
  isEdit?: boolean;
  request?: () => void;
  oriFilter?: any;
  promptInfo?: any;
}) {
  const {
    gameInfo,
    filter,
    toggleVisible,
    isEdit = false,
    request,
    oriFilter,
    promptInfo,
  } = props;

  const lang = getLocale();
  const { formatMessage } = useLanguage();

  const [name, setName] = useState({
    name: filter?.name || '',
    name_cn: filter?.name_cn || ''
  });
  // TODO: 屏蔽Email getAiSaveEmailList(userInfo)
  const [emailList, setEmailList] = useState<Array<string>>([]);
  const [showEmailMessage, setShowEmailMessage] = useState(false);

  const intervalType = useMemo(() => {
    if (filter?.interval_unit) {
      let intervalType = 'daily';
      if (filter.interval_unit === 'month') {
        intervalType = 'monthly';
      } else if (filter.interval_unit === 'week') {
        intervalType = 'weekly';
      } else if (filter.interval_unit === 'hour') {
        intervalType = 'hourly';
      }
      return intervalType;
    }
    return undefined;
  }, [filter]);

  const [repeatDateRange, setRepeatDateRange] = useState({
    start_time:
      filter?.start_date || filter?.start_time || moment().format('YYYY-MM-DD'),
    end_time:
      filter?.end_date || filter?.end_time || moment().format('YYYY-MM-DD'),
    date_type: intervalType || filter?.date_type || 'daily',
  });
  const [isFirst, setIsFirst] = useState(true);

  const defaultEndTime = useMemo(
    () =>
      filter?.cycle_end_date
        ? moment(filter?.cycle_end_date)
        : moment(repeatDateRange.start_time).add(1, 'year'),
    [filter, repeatDateRange],
  );

  const [endTime, setEndTime] = useState<[string, string]>([
    moment(defaultEndTime).format('YYYY-MM-DD HH:mm:ss'),
    moment(defaultEndTime).format('YYYY-MM-DD HH:mm:ss'),
  ]);

  // 获取时间的开始和结束时间 如果改变日期，这里value会返回[null, null]
  const onChangeRangePicker = (value: Array<Moment>) => {
    const hasTime = value.every(Boolean);
    let newValue: any = hasTime ? value : endTime;
    newValue = newValue.map((item: string) =>
      (typeof item === 'string' ? moment(item) : item).format(
        'YYYY-MM-DD HH:mm:ss',
      ),
    );
    if (!(newValue[0] === endTime[0] && newValue[1] === endTime[1])) {
      if (newValue[0] === endTime[0]) {
        setEndTime([newValue[1], newValue[1]]);
      }
      else {
        setEndTime([newValue[0], newValue[0]]);
      }
    }
  };

  const { diff, dateTips } = useMemo(() => {
    const { start_time, end_time, date_type } = repeatDateRange;
    const momentDateType = paramsToMomentMap?.[date_type || 'daily'];
    const diffValue = moment(end_time).diff(moment(start_time), momentDateType, true);
    const diff =
      filter?.interval_value || Math.ceil(diffValue);
    const dateType = diff > 1 ? momentDateType : momentDateType?.slice(0, -1);

    if (diff === 0) {
      return {
        diff,
        dateTips: formatMessage({ id: 'Please select at least a 1-hour interval' })
      };
    }

    if (date_type === 'hourly') {
      return {
        diff,
        dateTips: formatMessage(
          { id: 'Repeat every {number} {hour}' },
          { number: diff > 1 ? diff : '', hour: formatMessage({ id: dateType }) },
        )
      };
    }

    if (date_type === 'monthly') {
      return {
        diff,
        dateTips: formatMessage(
          {
            id: 'Repeat every {number} {dateType}, data spans from 00:00 on the first day of Month 1 to 00:00 on the first day of Month {totalDiff}',
          },
          {
            number: diff > 1 ? diff : '',
            dateType: formatMessage({ id: dateType }),
            totalDiff: diff + 1,
          },
        )
      };
    }

    return {
      diff,
      dateTips: formatMessage(
        {
          id: 'Repeat every {number} {dateType}, data spans from 00:00 on {dateTypeText} 1 to 00:00 on {dateTypeText} {totalDiff}',
        },
        {
          number: diff > 1 ? diff : '',
          dateType: formatMessage({ id: dateType }),
          dateTypeText: formatMessage({
            id: initialUpperCase(momentDateType.slice(0, -1)),
          }),
          totalDiff: diff + 1,
        },
      )
    };
  }, [formatMessage, repeatDateRange]);

  const repeatFilter = useMemo(() => {
    let localFilter: any = {
      ...repeatDateRange,
      cycle_end_date: endTime[0],
      ...name,
      ...(isEdit ? { id: filter?.id } : {}),
    };

    if (emailList.length > 0) {
      localFilter = {
        ...localFilter,
        email: emailList.join(','),
      };
    }

    return { ...localFilter, link_url_domain: getLinkDomain() };
  }, [endTime, filter, isEdit, name, repeatDateRange, emailList]);

  const { createTask, updateTask } = useCreateRepeatSummaryTask({
    filter,
    gameInfo,
    toggleVisible,
    repeatFilter,
    formatMessage,
    request,
    oriFilter,
    promptInfo,
  });

  return (
    <>
      <Form className="dbo-repeat-setting">
        <Form.Item
          label={formatMessage({ id: 'Repeat Date Range' })}
          className="repeat-setting-item"
        >
          <div className={`${isEdit ? 'dbo-filterbox-preview' : ''}`}>
            <DateSelect
              lang={getLocale()}
              gameInfo={gameInfo}
              filterOptions={{ time: filter?.time }}
              defaultValue={repeatDateRange}
              onChange={(timeKey: any, result: any) => {
                if (result?.value?.start_time && result?.value?.end_time) {
                  setRepeatDateRange(result?.value);
                }
              }}
            />
            <span className="dbo-repeat-setting-date-tips">{dateTips}</span>
          </div>
        </Form.Item>
        <Form.Item label={formatMessage({ id: 'Repeat End Time' })}>
          <div
            className="date-select-wrapper search-item search-item-date"
            style={{ display: 'inline-block' }}
          >
            <RangePicker
              autoStartDay={false}
              onChange={onChangeRangePicker}
              resultDateFormat={{
                Day: 'YYYY-MM-DD',
                Week: 'YYYY-MM-DD',
                Month: 'YYYY-MM-DD',
              }}
              defaultValue={defaultRangePickerValue()}
              className={'search-border'}
              value={[moment(endTime[0]), moment(endTime[1])]}
              typeList={['1day']}
              disables={false}
              language={lang === 'zh-CN' ? 'cn' : 'en'}
              type={'1day'}
              hourLeftSide={false}
              optionsMap={{
                '1day': { value: '1day', text: '', text_cn: '' },
              }}
              showSelectType={false}
            />
          </div>
        </Form.Item>
        <GPT.GPTSummaryMultiNameForm
          name={name}
          setName={(value: string, key = 'name') => setName({ ...name, [key]: value })}
          setEmailList={setEmailList}
          emailList={emailList}
          showEmailMessage={showEmailMessage}
          setShowEmailMessage={setShowEmailMessage}
          needSummaryName
          placeholder={formatMessage({
            id: 'Please enter a name for this report',
          })}
          isFirst={isFirst}
        ></GPT.GPTSummaryMultiNameForm>
      </Form>
      <div className="dbo-repeat-setting-footer">
        <Button
          type="primary"
          disabled={
            (!name?.name && !isFirst) || showEmailMessage || diff === 0
          }
          onClick={() => {
            setIsFirst(false);
            if (name) {
              isEdit ? updateTask() : createTask();
              buriedBtnPoint('ConfirmGenerateAndRepeat');
            }
          }}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={toggleVisible}>
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </div>
    </>
  );
}
